import type { Driver, DriveStep } from 'driver.js';
import type { I18n } from '@lingui/core';
import type { TourPath, TourStep } from '@pantry/shared';
import { getTourById } from '@pantry/api-client';
import { translateStepField } from '@pantry/i18n';
import { getPathFilteredSteps } from './tour-context.helpers';

/**
 * Convert tour definition steps to Driver.js format, filtered by path.
 */
export function convertToDriverSteps(
  i18n: I18n,
  tourId: string,
  path: TourPath | null
): DriveStep[] {
  const definition = getTourById(tourId);
  const filteredSteps = getPathFilteredSteps(definition, path);

  return filteredSteps.map((step) => ({
    element: step.target,
    popover: {
      title: translateStepField(i18n, tourId, step.id, 'title', step.title),
      description: translateStepField(i18n, tourId, step.id, 'content', step.content),
      side: step.placement === 'center' ? 'over' : step.placement,
      align: 'center',
    },
  }));
}

/**
 * Get filtered tour steps for the given tour and path.
 */
export function getFilteredSteps(tourId: string, path: TourPath | null): TourStep[] {
  const definition = getTourById(tourId);
  return getPathFilteredSteps(definition, path);
}

/**
 * Wait briefly for a target element to exist before starting Driver.
 * This avoids first-step centering when UI is conditionally rendered by tour state.
 */
export function waitForTourTarget(selector: string, timeoutMs = 1000): Promise<void> {
  if (!selector || typeof document === 'undefined') {
    return Promise.resolve();
  }

  if (document.querySelector(selector)) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const start = Date.now();
    const poll = () => {
      if (document.querySelector(selector) || Date.now() - start >= timeoutMs) {
        resolve();
        return;
      }
      window.requestAnimationFrame(poll);
    };
    window.requestAnimationFrame(poll);
  });
}

/**
 * Wait for a target element to render, then advance Driver.js to that step.
 * The isStillActive check prevents stale drivers from advancing after being replaced.
 */
export async function driveTourStepWhenReady(
  driverInstance: Driver,
  isStillActive: () => boolean,
  tourId: string,
  stepIndex: number,
  path: TourPath | null
): Promise<void> {
  const steps = getFilteredSteps(tourId, path);
  const target = steps[stepIndex]?.target;
  if (target) {
    await waitForTourTarget(target);
  }

  if (!isStillActive()) return;

  driverInstance.drive(stepIndex);
}

/**
 * Create a pause button DOM element for tour steps with canPause.
 */
export function createPauseButton(options: {
  label: string;
  onPause: () => void;
}): HTMLButtonElement {
  const pauseBtn = document.createElement('button');
  pauseBtn.className = 'tour-pause-btn driver-popover-btn';
  pauseBtn.textContent = options.label;
  pauseBtn.style.cssText = 'margin-left: 8px; opacity: 0.7; font-size: 0.875rem; padding: 4px 8px;';
  pauseBtn.onclick = options.onPause;
  return pauseBtn;
}

/**
 * Create path choice DOM elements for branching tours (create vs join household).
 */
export function createPathChoiceElement(options: {
  createLabel: string;
  joinLabel: string;
  onCreatePath: () => void;
  onJoinPath: () => void;
}): HTMLDivElement {
  const { createLabel, joinLabel, onCreatePath, onJoinPath } = options;

  const choiceContainer = document.createElement('div');
  choiceContainer.className = 'tour-path-choice';
  choiceContainer.style.cssText = 'display: flex; flex-direction: column; gap: 10px; width: 100%;';

  // Create household button (primary action)
  const createBtn = document.createElement('button');
  createBtn.className = 'tour-path-btn';
  createBtn.textContent = createLabel;
  createBtn.style.cssText =
    'width: 100%; padding: 12px 16px; font-size: 0.9rem; font-weight: 500; background: #16a34a; color: white; border: none; border-radius: 8px; cursor: pointer; transition: opacity 0.2s;';
  createBtn.onmouseenter = () => {
    createBtn.style.opacity = '0.9';
  };
  createBtn.onmouseleave = () => {
    createBtn.style.opacity = '1';
  };
  createBtn.onclick = onCreatePath;

  // Join/waiting button (secondary action)
  const joinBtn = document.createElement('button');
  joinBtn.className = 'tour-path-btn';
  joinBtn.textContent = joinLabel;
  joinBtn.style.cssText =
    'width: 100%; padding: 12px 16px; font-size: 0.9rem; background: transparent; color: inherit; border: 1px solid rgba(128,128,128,0.4); border-radius: 8px; cursor: pointer; transition: all 0.2s;';
  joinBtn.onmouseenter = () => {
    joinBtn.style.borderColor = 'rgba(128,128,128,0.7)';
  };
  joinBtn.onmouseleave = () => {
    joinBtn.style.borderColor = 'rgba(128,128,128,0.4)';
  };
  joinBtn.onclick = onJoinPath;

  choiceContainer.appendChild(createBtn);
  choiceContainer.appendChild(joinBtn);

  return choiceContainer;
}
